import type { ReactNode } from "react";

import { ApiError } from "../api/client";
import { useI18n, type Translate } from "../i18n";

export function Badge({ value }: { value: string }) {
  const { t } = useI18n();
  return (
    <span className={`badge ${value}`}>
      {t(`status.${value}`, undefined, value.replace(/_/g, " "))}
    </span>
  );
}

/**
 * A status as a coloured dot beside its name. Lighter than the bordered
 * Badge for a dense list, where the dot alone carries the state while the
 * eye skims down a column.
 */
/**
 * A phone's state: a coloured dot and the word for it.
 *
 * `compact` keeps the dot and moves the word to the tooltip — what the fleet
 * list wears, where the column beside it is already a status filter naming all
 * five and the row has icons to fit. The word stays in the accessibility tree
 * either way; the dot alone is never the only telling.
 */
export function StatusDot({ value, compact }: { value: string; compact?: boolean }) {
  const { t } = useI18n();
  const label = t(`status.${value}`, undefined, value.replace(/_/g, " "));
  return (
    <span className={`status-dot ${value}`} title={compact ? label : undefined}>
      <i aria-hidden="true" />
      {compact ? <span className="sr-only">{label}</span> : label}
    </span>
  );
}

export function Stat({ label, value, tone }: { label: string; value: ReactNode; tone?: string }) {
  return (
    <div className="card stat">
      <div className="value" style={tone ? { color: `var(--${tone})` } : undefined}>
        {value}
      </div>
      <div className="label">{label}</div>
    </div>
  );
}

export function Loading({ what }: { what: string }) {
  const { t } = useI18n();
  return <p className="muted">{t("loading", { what })}</p>;
}

export function ErrorBox({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : String(error);
  const issues = error instanceof ApiError ? error.issues : undefined;
  return (
    <div>
      <p className="error-text">{message}</p>
      {issues && issues.length > 0 && (
        <ul className="error-text" style={{ margin: "4px 0", paddingLeft: 18 }}>
          {issues.map((issue, index) => (
            <li key={index}>
              {issue.path && <span className="mono">{issue.path} — </span>}
              {issue.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="muted">{children}</p>;
}

export function relativeTime(iso: string | null, t: Translate): string {
  if (!iso) return "—";
  const seconds = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return t("time.secondsAgo", { n: seconds });
  if (seconds < 3600) return t("time.minutesAgo", { n: Math.round(seconds / 60) });
  if (seconds < 86400) return t("time.hoursAgo", { n: Math.round(seconds / 3600) });
  return t("time.daysAgo", { n: Math.round(seconds / 86400) });
}

/** `1.4 MB` — 1024-based, because that is what a file manager shows beside the
 *  same file. Whole bytes below a kilobyte; one decimal above. */
export function formatBytes(bytes: number): string {
  if (!bytes) return "0";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** index).toFixed(index ? 1 : 0)} ${units[index]}`;
}

export function duration(start: string | null, end: string | null): string {
  if (!start || !end) return "—";
  const seconds = (new Date(end).getTime() - new Date(start).getTime()) / 1000;
  return seconds < 60 ? `${seconds.toFixed(1)}s` : `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
}
